import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import type { GetFormConfigurationResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetFormConfigurationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetFormConfigurationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetFormConfigurationArgs = {
  parentTypes: ParentType[];
};

/**
 * Flag-gated `getFormConfiguration` read over the `getByParentTypes` tRPC
 * procedure.
 *
 * The GraphQL fallback deliberately keeps the original `where`-based
 * `GetFormConfigurationDocument` (NOT the `...ByParentType` document), so a
 * `parentTypes` arg maps back to its `where` filter and existing
 * `GetFormConfigurationDocument` test mocks keep matching. An empty
 * `parentTypes` array means "no filter" (all forms) — matching both the legacy
 * unscoped `where: {}` and the data-layer's empty-array semantics on
 * `getByParentTypes`.
 */
export const useGetFormConfiguration = createQueryHook<
  UseGetFormConfigurationArgs,
  GetFormConfigurationResponseRow[],
  GetFormConfigurationQuery
>({
  trpcQueryOptions: (trpc, { parentTypes }) =>
    trpc.frontend.formConfiguration.getByParentTypes.queryOptions({
      parentTypes,
    }),
  mapTrpcDataToGraphQL: (data) => ({ form_configuration: data }),
  graphqlDocument: GetFormConfigurationDocument,
  graphqlVariables: ({ parentTypes }) =>
    // Empty => omit `where` entirely (the query defaults it to `{}`),
    // reproducing the legacy unscoped `useQuery(GetFormConfigurationDocument)`
    // call's variable shape so the existing test mocks keep matching.
    parentTypes.length ? { where: { ParentType: { _in: parentTypes } } } : {},
});
